import React, { useEffect, useState, useRef } from 'react';
import { formatBuyerOrderStatus } from '../../utils/orderStatus';
import { formatNPR } from '../../utils/currency';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import useFetch from '../../utils/useFetch';
import convertToQueryParams from '../../utils/convertToQueryParams';
import getImageUrl from '../../utils/getImageUrl';
import useFilters from '../../hooks/useFilters';
import Pagination from '../pagination/Pagination';

const Dashboard = () => {
	const ordersListRef = useRef(null);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const Api = `sales/order-summaries/`;
	const [searchParams, setSearchParams] = useSearchParams();

	const initialFilters = {
		ordering: searchParams.get('ordering') || '-created_at',
		is_active: searchParams.get('is_active') || null,
		page: parseInt(searchParams.get('page')) || 1,
		size: parseInt(searchParams.get('size')) || 10,
		search: searchParams.get('search') || null,
		tags: searchParams.get('tags') || null,
		status: searchParams.get('status') || null,
	};

	const {
		filters,
		setFilters,
	} = useFilters(initialFilters);

	const [expandedOrder, setExpandedOrder] = useState(null);
	const [searchInput, setSearchInput] = useState(filters.search || '');

	const { data, loading, error, headers } = useFetch(`${Api}${convertToQueryParams(filters)}`);

	// Effect to expand first item when data or filters change
	useEffect(() => {
		if (data?.results?.length > 0) {
			setExpandedOrder(data.results[0].id);
		}
	}, [data?.results]);

	const updateURL = (newFilters) => {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(newFilters)) {
			if (value) {
				params.set(key, value);
			} else {
				params.delete(key);
			}
		}
		setSearchParams(params);
	};

	const handleSort = (value) => {
		const newFilters = { ...filters, ordering: value, page: 1 };
		setFilters(newFilters);
		updateURL(newFilters);
	};

	const onPageChange = (page) => {
		const newFilters = { ...filters, page };
		setFilters(newFilters);
		updateURL(newFilters);
		
		// Scroll to the top of the orders list with smooth animation
		ordersListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const handleStatusFilter = (status) => {
		const newFilters = { ...filters, status, page: 1 };
		setFilters(newFilters);
		updateURL(newFilters);
	};

	const clearFilters = () => {
		const newFilters = {
			ordering: '-created_at',
			is_active: null,
			page: 1,
			size: 10,
			search: null,
			tags: null,
			status: null,
			days: 30,
		};
		setFilters(newFilters);
		setSearchInput('');
		updateURL(newFilters);
	};

	const handleSearchInputChange = (event) => {
		setSearchInput(event.target.value);
	};

	const handleSearchClick = () => {
		const newFilters = { ...filters, search: searchInput, page: 1 };
		setFilters(newFilters);
		updateURL(newFilters);
	};

	const toggleOrder = (orderId) => {
		setExpandedOrder(expandedOrder === orderId ? null : orderId);
	};

	const getStatusColor = (status) => {
		switch (status?.toLowerCase()) {
			case 'completed':
				return 'bg-brand-600';
			case 'processing':
				return 'bg-amber-600';
			case 'shipped':
				return 'bg-sky-600';
			case 'cancelled':
				return 'bg-red-600';
			default:
				return 'bg-gray-500';
		}
	};

	return (
		<div className="overflow-hidden">
			<h2 className="text-xl text-black font-semibold mb-5">Dashboard</h2>
			
			{/* Search and Filter Section */}
			<div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center">
				<div className="flex flex-1 gap-2">
					<input
						type="text"
						placeholder="Search orders..."
						value={searchInput}
						onChange={handleSearchInputChange}
						className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
					/>
					<button
						onClick={handleSearchClick}
						className="p-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
					>
						Search
					</button>
					<button
						onClick={clearFilters}
						className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
					>
						Clear
					</button>
				</div>
				
				{/* Sort and Filter Options */}
				<div className="flex gap-2">
					{/* <select
						className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
						value={filters.status || ''}
						onChange={(e) => handleStatusFilter(e.target.value === '' ? null : e.target.value)}
					>
						<option value="">All Status</option>
						<option value="processing">Processing</option>
						<option value="shipped">Shipped</option>
						<option value="completed">Completed</option>
					</select> */}

					<select
						className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
						value={filters.ordering || '-created_at'}
						onChange={(e) => handleSort(e.target.value)}
					>
						<option value="-created_at">Newest First</option>
						<option value="created_at">Oldest First</option>
						<option value="-bill_amount">Highest Amount</option>
						<option value="bill_amount">Lowest Amount</option>
						<option value="-order_number">Order ID (High to Low)</option>
						<option value="order_number">Order ID (Low to High)</option>
						<option value="status">Status (A-Z)</option>
						<option value="-status">Status (Z-A)</option>
					</select>
				</div>
			</div>

			<div className="grid gap-4 mb-8 md:grid-cols-2 xl:grid-cols-4">
				{/* Total Order Card */}
				<div className="min-w-0 flex-1" onClick={clearFilters}>
					<div className="flex items-center border border-gray-200 w-full rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors">
						<div className="flex-shrink-0 flex items-center justify-center p-3 rounded-full h-12 w-12 text-xl text-center mr-4 text-red-600 bg-red-200">
							<svg
								stroke="currentColor"
								fill="none"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
								height="1em"
								width="1em"
								xmlns="http://www.w3.org/2000/svg"
							>
								<circle cx="9" cy="21" r="1"></circle>
								<circle cx="20" cy="21" r="1"></circle>
								<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
							</svg>
						</div>
						<div className="min-w-0 flex-1">
							<h5 className="truncate text-sm font-medium text-gray-700 mb-1">
								Total Order
							</h5>
							<p className="text-xl font-bold text-gray-800">
								{headers?.['response-properties']?.processing +
									headers?.['response-properties']?.shipped +
									headers?.['response-properties']?.delivered}
							</p>
						</div>
					</div>
				</div>

				{/* Processing Order Card */}
				<div className="min-w-0 flex-1" onClick={() => handleStatusFilter('processing')}>
					<div className="flex items-center border border-gray-200 w-full rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors">
						<div className="flex-shrink-0 flex items-center justify-center p-3 rounded-full h-12 w-12 text-xl text-center mr-4 text-orange-600 bg-orange-200">
							<svg
								stroke="currentColor"
								fill="none"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
								height="1em"
								width="1em"
								xmlns="http://www.w3.org/2000/svg"
							>
								<polyline points="23 4 23 10 17 10"></polyline>
								<polyline points="1 20 1 14 7 14"></polyline>
								<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
							</svg>
						</div>
						<div className="min-w-0 flex-1">
							<h5 className="truncate text-sm font-medium text-gray-700 mb-1">
								Processing Order
							</h5>
							<p className="text-xl font-bold text-gray-800">
								{headers?.['response-properties']?.processing}
							</p>
						</div>
					</div>
				</div>

				{/*  For the cancelled order card */}
				<div className="min-w-0 flex-1" onClick={() => handleStatusFilter('cancelled')}>
					<div className="flex items-center border border-gray-200 w-full rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors">
						<div className="flex-shrink-0 flex items-center justify-center p-3 rounded-full h-12 w-12 text-xl text-center mr-4 text-red-600 bg-red-200">
							<svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
							<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
							<polyline points="22 4 12 14.01 9 11.01"></polyline>
						</svg>
						</div>
						<div className="min-w-0 flex-1">
							<h5 className="truncate text-sm font-medium text-gray-700 mb-1">
								Cancelled Order
							</h5>
							<p className="text-xl font-bold text-gray-800">
								{headers?.['response-properties']?.cancelled}
							</p>
						</div>
					</div>
				</div>

				{/* Shipped Order Card */}
				<div className="min-w-0 flex-1" onClick={() => handleStatusFilter('shipped')}>
					<div className="flex items-center border border-gray-200 w-full rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors">
						<div className="flex-shrink-0 flex items-center justify-center p-3 rounded-full h-12 w-12 text-xl text-center mr-4 text-brand-700 bg-brand-100">
							<svg
								stroke="currentColor"
								fill="none"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
								height="1em"
								width="1em"
								xmlns="http://www.w3.org/2000/svg"
							>
								<line x1="22" y1="12" x2="2" y2="12"></line>
								<path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
								<line x1="6" y1="16" x2="6.01" y2="16"></line>
								<line x1="10" y1="16" x2="10.01" y2="16"></line>
							</svg>
						</div>
						<div className="min-w-0 flex-1">
							<h5 className="truncate text-sm font-medium text-gray-700 mb-1">
								Shipped Order
							</h5>
							<p className="text-xl font-bold text-gray-800">
								{headers?.['response-properties']?.shipped}
							</p>
						</div>
					</div>
				</div>

				{/* Complete Order Card */}
				<div className="min-w-0 flex-1" onClick={() => handleStatusFilter('delivered')}>
					<div className="flex items-center border border-gray-200 w-full rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors">
						<div className="flex-shrink-0 flex items-center justify-center p-3 rounded-full h-12 w-12 text-xl text-center mr-4 text-brand-600 bg-brand-200">
							<svg
								stroke="currentColor"
								fill="none"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
								height="1em"
								width="1em"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
								<polyline points="22 4 12 14.01 9 11.01"></polyline>
							</svg>
						</div>
						<div className="min-w-0 flex-1">
							<h5 className="truncate text-sm font-medium text-gray-700 mb-1">
								Complete Order
							</h5>
							<p className="text-xl font-bold text-gray-800">
								{headers?.['response-properties']?.delivered}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Orders List */}
			<div ref={ordersListRef} className="space-y-4 mb-6">
				{loading ? (
					<div className="text-center py-10">Loading...</div>
				) : error ? (
					<div className="text-center py-10 text-red-500">Error loading orders</div>
				) : data?.results?.length === 0 ? (
					<div className="text-center py-10 text-gray-500">No orders found</div>
				) : (
					data?.results?.map((order) => (
						<div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
							<div 
								className="p-4 cursor-pointer hover:bg-gray-50 transition-all"
								onClick={() => toggleOrder(order.id)}
							>
								<div className="flex justify-between items-start">
									<div className="space-y-1">
										<div className="flex items-center space-x-4">
											<div>
												<p className="text-sm text-gray-500">Order No</p>
												<p className="font-medium">{order.order_number}</p>
											</div>
											<div>
												<p className="text-sm text-gray-500">Order Placed</p>
												<p className="font-medium">{moment(order.created_at).format('DD MMM YYYY')}</p>
											</div>
											<div>
												<p className="text-sm text-gray-500">Status</p>
												<div className="flex items-center space-x-2">
													<span className={`w-2 h-2 rounded-full ${getStatusColor(order.status)}`}></span>
													<span className="capitalize">{formatBuyerOrderStatus(order.status)}</span>
												</div>
											</div>
										</div>
									</div>
									<div className="flex items-center space-x-4">
										<div className="text-right">
											<p className="text-sm text-gray-500">Total</p>
											<p className="font-semibold text-lg">{formatNPR(order.bill_amount)}</p>
										</div>
										<button 
											className="text-gray-400 hover:text-gray-600 transition-colors"
											onClick={(e) => {
												e.stopPropagation();
												toggleOrder(order.id);
											}}
										>
											<svg 
												className={`w-6 h-6 transform transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`} 
												fill="none" 
												stroke="currentColor" 
												viewBox="0 0 24 24"
											>
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
											</svg>
										</button>
									</div>
								</div>
							</div>

							{/* Collapsible content */}
							<div 
								className={`transition-all duration-300 ease-in-out overflow-hidden ${
									expandedOrder === order.id ? 'max-h-96' : 'max-h-0'
								}`}
							>
								<div className="p-4 pt-0 border-t border-gray-100">
									<div className="mt-4">
										<div className="text-sm text-gray-500">Order Summary ({order.order_items_highlights?.length} items)</div>
										<div className="mt-2 space-y-2">
											{order.order_items_highlights?.map((item, index) => (
												<div key={index} className="flex items-center space-x-4">
													<img 
														src={getImageUrl(item.thumbnail_image)} 
														alt={item.product_name}
														className="w-12 h-12 object-cover rounded"
													/>
													<div>
														<p className="font-medium">{item.product_name}</p>
														<p className="text-sm text-gray-500">SKU: {item.product_sku} • Qty: {item.quantity}</p>
													</div>
												</div>
											))}
										</div>
									</div>

									<div className="mt-4 flex justify-between items-center text-sm">
										<div>
											<span className="text-gray-500">Subtotal:</span> {formatNPR(order.sub_total_amount)}
											{order.discount_amount > 0 && (
												<> • <span className="text-gray-500">Discount:</span> {formatNPR(order.discount_amount)}</>
											)}
											{order.service_charge > 0 && (
												<> • <span className="text-gray-500">Shipping:</span> {formatNPR(order.service_charge)}</>
											)}
										</div>
										<Link
											to={`/order/${order.order_number}`}
											className="text-brand-600 hover:text-brand-700 font-medium"
											onClick={(e) => e.stopPropagation()}
										>
											View Details
										</Link>
									</div>
								</div>
							</div>
						</div>
					))
				)}
			</div>

			<Pagination
				className="pagination-bar"
				currentPage={filters.page}
				totalCount={data?.count || 0}
				pageSize={filters.size}
				onPageChange={(page) => onPageChange(page)}
			/>
		</div>
	);
};

export default Dashboard;